import { Link } from "@tanstack/react-router";
import { MapPin, Star, Sparkles } from "lucide-react";
import type { PublicChef } from "@/lib/chefs.queries";
import { TierBadge, VerifiedBadge, FounderBadge, SponsoredBadge } from "./chef-badges";

export function ChefCard({ chef }: { chef: PublicChef }) {
  const cover = chef.cover_url;
  const priceFrom = chef.min_price ?? chef.hourly_rate;
  const initials = chef.display_name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <Link
      to="/chef/$slug"
      params={{ slug: chef.slug }}
      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-all hover:-translate-y-1 hover:border-gold hover:shadow-xl"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {cover ? (
          <img
            src={cover}
            alt={`${chef.headline ?? chef.display_name} — ${chef.city ?? ""}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="grid h-full w-full place-items-center bg-gradient-to-br from-secondary to-muted">
            <span className="font-display text-5xl text-muted-foreground/60">{initials || "👨‍🍳"}</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {chef.is_sponsored && <SponsoredBadge />}
          <TierBadge tier={chef.premium_tier} />
          {chef.verified_badge && <VerifiedBadge />}
          {chef.founder_badge && <FounderBadge />}
        </div>
        {priceFrom != null && (
          <div className="absolute bottom-3 right-3 rounded-md bg-background/95 px-2.5 py-1 text-xs font-medium backdrop-blur">
            da <span className="font-display text-base">€{Math.round(priceFrom)}</span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-secondary text-xs font-semibold ring-2 ring-border">
            {initials || "C"}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-display text-lg leading-tight">{chef.display_name}</h3>
            <p className="truncate text-xs text-muted-foreground">{chef.headline ?? chef.specialties[0] ?? "Chef privato"}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {chef.rating_count > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Star className="h-3.5 w-3.5 fill-gold text-gold" />
              <span className="font-medium text-foreground">{chef.rating_avg.toFixed(1)}</span>
              <span>({chef.rating_count})</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 font-medium text-foreground">
              <Sparkles className="h-3.5 w-3.5 text-gold" />
              Nuovo su ChefConnect
            </span>
          )}
          {chef.city && (
            <>
              <span aria-hidden>•</span>
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {chef.city}
              </span>
            </>
          )}
        </div>

        <div className="mt-auto flex flex-wrap gap-1.5">
          {chef.cuisines.slice(0, 3).map((c) => (
            <span key={`c-${c}`} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-secondary-foreground">
              {c}
            </span>
          ))}
          {chef.specialties.slice(0, 3).map((s) => (
            <span key={`s-${s}`} className="rounded-full border border-gold/40 bg-gold-muted/30 px-2.5 py-0.5 text-xs text-foreground">
              {s}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}